import { z } from 'zod';

/**
 * Design tokens (architecture documentation section 17).
 *
 * Themes are validated configuration, never stylesheets or code. The frontend
 * projects these tokens onto CSS custom properties, so a client rebrand never
 * requires a source fork (principle 5.6).
 */
const hexColor = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'must be a hex color');

export const colorTokensSchema = z.object({
  primary: hexColor,
  primaryContrast: hexColor,
  accent: hexColor,
  background: hexColor,
  surface: hexColor,
  surfaceMuted: hexColor,
  border: hexColor,
  text: hexColor,
  textMuted: hexColor,
  success: hexColor,
  warning: hexColor,
  danger: hexColor,
  info: hexColor,
});
export type ColorTokens = z.infer<typeof colorTokensSchema>;

export const themeSchema = z.object({
  themeVersion: z.literal(1),
  brand: z.object({
    name: z.string().min(1).max(120),
    /**
     * An optional brand mark, shown beside the name in the application header.
     *
     * Constrained to absolute http(s) URLs and same-origin paths, and nothing
     * else. It was `z.string().max(512)`, which accepts `javascript:` and
     * `data:` -- and the value is set by a provider administrator and then
     * rendered into every client's page, so a loose schema here is a stored
     * injection waiting for the day somebody puts it in an `<a href>` rather
     * than an `<img src>`. Validating the shape at the contract is the fix that
     * holds wherever it is eventually rendered.
     */
    logoUrl: z
      .string()
      .max(512)
      .regex(/^(https:\/\/|http:\/\/|\/)/, 'must be an http(s) URL or a same-origin path')
      .optional(),
  }),
  colors: z.object({ light: colorTokensSchema, dark: colorTokensSchema }),
  typography: z.object({
    fontFamily: z.string().min(1).max(240),
    monoFamily: z.string().min(1).max(240).default('ui-monospace, SFMono-Regular, monospace'),
    baseSize: z.number().min(12).max(20).default(16),
  }),
  shape: z.object({
    radius: z.number().min(0).max(24).default(8),
    density: z.enum(['comfortable', 'compact']).default('comfortable'),
  }),
  /**
   * Categorical series colors, stepped separately for each surface.
   *
   * Kept apart from the semantic colors because the two answer different
   * questions: semantic colors say what a value means, series colors only say
   * which line is which. Brand colors are usually a poor categorical palette --
   * they are chosen to harmonize, and harmonious hues are exactly the ones that
   * collapse into each other under colour-vision deficiency.
   *
   * The dark column is the same hues re-stepped for the dark surface, not an
   * inversion: automatically lightening a light-mode palette produces steps that
   * fail contrast against a dark background.
   *
   * A replacement palette must be validated for lightness band, chroma floor,
   * adjacent-pair CVD separation and contrast before it is deployed -- these
   * defaults were.
   */
  chartPalette: z.object({
    light: z.array(hexColor).min(4).max(8),
    dark: z.array(hexColor).min(4).max(8),
  }),
});
export type Theme = z.infer<typeof themeSchema>;

export const DEFAULT_THEME: Theme = {
  themeVersion: 1,
  brand: { name: 'Dashboard' },
  colors: {
    light: {
      primary: '#2A5DB0',
      primaryContrast: '#FFFFFF',
      accent: '#0F766E',
      background: '#F6F7F9',
      surface: '#FFFFFF',
      surfaceMuted: '#EFF1F5',
      border: '#D6DAE1',
      text: '#171A1F',
      textMuted: '#5B6472',
      success: '#1E7A46',
      warning: '#96620A',
      danger: '#B42318',
      info: '#1F6FB2',
    },
    dark: {
      primary: '#7FA8E8',
      primaryContrast: '#0B1220',
      accent: '#4FD1C5',
      background: '#0F141B',
      surface: '#171E28',
      surfaceMuted: '#1F2833',
      border: '#2E3947',
      text: '#E9EDF3',
      textMuted: '#9AA6B6',
      success: '#4ECB84',
      warning: '#E0A83A',
      danger: '#F0736A',
      info: '#6FB6EE',
    },
  },
  typography: {
    fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, sans-serif',
    monoFamily: 'ui-monospace, SFMono-Regular, monospace',
    baseSize: 16,
  },
  shape: { radius: 8, density: 'comfortable' },
  chartPalette: {
    light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'],
    dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181'],
  },
};
